<template>
  <table class="strip">
    <thead>
      <tr>
        <th
          class="clickable"
          :class="{ active: viewSettings[0] === 'nameSort', nosort: !showSort }"
          @click="sortList('nameSort')"
        >
          <span>{{ $t('sidebar.stock-list-header-name') }}</span>
          <sort-arrows
            v-if="showSort && viewSettings[0]"
            :active-method="viewSettings[0]"
            :sort-direction="viewSettings[1]"
            :sort-method="'nameSort'"
          />
        </th>
        <th>
          {{ $t('trade.abbr-holdings') + '|' + $t('trade.abbr-market-value') }}
        </th>
        <th>{{ $t('trade.latestPrice') + '|' + $t('trade.cost') }}</th>
        <th
          class="clickable pl-head"
          :class="{ active: viewSettings[0] === 'pnl', nosort: !showSort }"
          @click="sortList('pnl')"
        >
          <span>{{ $t('trade.profit-and-loss') }}</span>
          <sort-arrows
            v-if="showSort && viewSettings[0]"
            :active-method="viewSettings[0]"
            :sort-direction="viewSettings[1]"
            :sort-method="'pnl'"
          />
        </th>
      </tr>
    </thead>
    <tbody>
      <template v-for="(stock, index) in displayList">
        <tr
          :key="stock.webLocal.symbol"
          :class="{ selected: index === activeIndex }"
          @click="setActive(stock, index)"
        >
          <td class="symbol-name">
            <div class="holding-name-wrap">
              <div :title="getSymbolName(stock).symbol" class="name-icon-wrap">
                <market-mark :market="stock.market" />
                <span>{{ getSymbolName(stock).symbol }}</span>
              </div>
              <p class="holding-nums">
                {{ stock.position | largeNumber }}
              </p>
            </div>
            <div class="symbol" :title="getSymbolName(stock).name">
              <span class="holing-symbol-text">{{
                getSymbolName(stock).name
              }}</span>
              <span class="market-value">{{
                stock.marketValue | classifyByQuantity
              }}</span>
            </div>
          </td>
          <td class="price-cost">
            <p v-blink="stock.latestPrice">
              {{ stock.latestPrice | floatNum(stock) }}
            </p>
            <p v-blink="stock.averageCost">
              {{ stock.averageCost | floatNum(stock) }}
            </p>
          </td>
          <td>
            <p
              v-blink="stock.unrealPnl"
              :class="stock.unrealPnl | redGreen"
              :title="stock.unrealPnl | precise | sign"
            >
              {{ stock.unrealPnl | largeNumber | sign }}
            </p>
            <p
              v-blink="stock.profitLossPer"
              :class="stock.profitLossPer | redGreen"
            >
              {{ stock.profitLossPer | percent }}
            </p>
          </td>
          <td class="operation-toggle">
            <span v-if="index === activeIndex">
              <icon name="arrowhead-up" />
            </span>
            <span v-else>
              <icon name="arrowhead-down" />
            </span>
          </td>
        </tr>
        <operation
          v-if="index === activeIndex"
          :key="`operation-${index}`"
          :holding="stock"
          @open="openDetail(stock)"
        />
      </template>
    </tbody>
  </table>
</template>
<script>
import MarketMark from 'mod/MarketMark'
import Icon from 'com/Icon'
import Operation from './Operation'
import orderBy from 'lodash/orderBy'
import SortArrows from 'mod/SortArrows'
import choiceFilter from 'utils/choiceFilter'
import { SEC_TYPE } from 'const/stock'
import { mapGetters } from 'vuex'
import getLocalSymbol from 'utils/getLocalSymbol'

export default {
  name: 'HoldingsTable',
  components: {
    MarketMark,
    Icon,
    Operation,
    SortArrows
  },
  data() {
    return {
      activeIndex: null,
      sortMethod: 'default',
      sortDirection: 'desc'
    }
  },
  props: {
    holdings: {
      required: true
    },
    settingId: String
  },
  computed: {
    ...mapGetters(['isIb']),
    showSort() {
      // 该股持仓不显示排序按钮
      return this.settingId !== 'InTrade'
    },
    list() {
      return this.holdings.map(t => {
        const holding = { ...t }
        holding.digits = holding.digits || choiceFilter(holding)
        if (holding.secType === SEC_TYPE.OPT) {
          holding.averageCost = this.isIb
            ? holding.averageCost / holding.multiplier
            : holding.averageCost
          holding.profitLoss =
            (holding.latestPrice - holding.averageCost) *
            holding.position *
            holding.multiplier
        } else {
          holding.profitLoss =
            (holding.latestPrice - holding.averageCost) * holding.position
        }
        if (holding.averageCost) {
          holding.profitLossPer =
            (holding.latestPrice - holding.averageCost) / holding.averageCost
        } else {
          holding.profitLossPer = 0
        }
        if (holding.position < 0) {
          holding.profitLossPer = -holding.profitLossPer
        }
        holding.webLocal = {
          symbol: getLocalSymbol(holding)
        }
        return holding
      })
    },
    displayList() {
      this.$debug('viewSettings: ', this.viewSettings)
      const displayList = this.list
      const sortMethod = this.viewSettings[0]
      const sortDirection = this.viewSettings[1]
      if (sortMethod === 'default') {
        return displayList
      }
      const sortMap = {
        nameSort: 'symbol',
        pnl: 'profitLoss'
      }
      return orderBy(displayList, [sortMap[sortMethod]], [sortDirection])
    },
    viewID() {
      return 'holdings' + this.settingId
    },
    viewSettings() {
      if (this.settingId === 'InTrade') return []
      return this.$store.getters.viewSettings[this.viewID]
    }
  },
  methods: {
    getSymbolName(stock) {
      if (this.isOption(stock)) {
        const { twoLine1, twoLine2 } = this.$options.filters.formatOptionName(
          stock
        )
        return {
          symbol: twoLine1,
          name: twoLine2
        }
      }
      return {
        name: stock.nameCN,
        symbol: stock.symbol
      }
    },
    isOption(stock) {
      return stock.secType === SEC_TYPE.OPT
    },
    sortList(method) {
      if (!this.showSort) return
      let sortMethod
      let sortDirection
      if (method === this.viewSettings[0]) {
        sortDirection = this.viewSettings[1] === 'desc' ? 'asc' : 'desc'
      } else {
        sortDirection = 'desc'
      }
      if (
        method === 'nameSort' &&
        this.viewSettings[0] === 'nameSort' &&
        this.viewSettings[1] === 'asc'
      ) {
        sortMethod = 'default'
      } else {
        sortMethod = method
      }
      const payload = {
        view: this.viewID,
        value: [sortMethod, sortDirection]
      }
      this.$store.dispatch('updateViewSettings', payload)
    },
    openDetail(stock) {
      this.$emit('open-detail', stock)
    },
    setActive(stock, index) {
      if (['SI', 'UK', 'AU'].includes(stock.market)) return
      if (this.list[index].secType === 'CASH') return
      if (this.activeIndex === index) {
        this.activeIndex = null
      } else {
        this.activeIndex = index
      }
    }
  },
  filters: {
    classifyByQuantity(value) {
      const reg = /(\d+)(\.?)/i
      const moneyLength = String(value).match(reg)[1].length
      if (moneyLength >= 4 && moneyLength < 7) {
        return (value / 1000).toFixed(1) + 'K'
      } else if (moneyLength < 5) {
        return value.toFixed(2)
      } else if (moneyLength >= 7) {
        return (value / 1000000).toFixed(1) + 'M'
      }
    }
  }
}
</script>

<style lang="less" scoped src="./styles.less"></style>
